import { createFileRoute, Link } from "@tanstack/react-router";
import { buildAlbumParam } from "@/lib/slugify";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { useState } from "react";
import { AppShell } from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import { ProBadge } from "@/components/ui/pro-badge";
import { searchAll } from "@/lib/search.functions";
import { searchArtists } from "@/lib/artists.functions";
import { Search, MapPin, Disc3, Users, Mic2 } from "lucide-react";

const schema = z.object({
  q: z.string().catch("").default(""),
  tab: z.enum(["people", "albums"]).catch("people").default("people"),
});

type SearchParams = z.infer<typeof schema>;

export const Route = createFileRoute("/search")({
  head: () => ({ meta: [{ title: "Buscar — Viny Club" }] }),
  validateSearch: schema,
  component: SearchPage,
});

function SearchPage() {
  const { q, tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [input, setInput] = useState(q);
  const fn = useServerFn(searchAll);

  const { data, isFetching } = useQuery({
    queryKey: ["search-all", q],
    queryFn: () => fn({ data: { q } }),
    enabled: q.length >= 2,
    staleTime: 60_000,
  });

  const people = data?.people ?? [];
  const albums = data?.albums ?? [];

  const artistsFn = useServerFn(searchArtists);
  const { data: artists } = useQuery({
    queryKey: ["search-artists", q],
    queryFn: () => artistsFn({ data: { q } }),
    enabled: q.length >= 2,
    staleTime: 60_000,
  });
  const artistList = artists ?? [];

  return (
    <AppShell>
      <PageHeader
        eyebrow="Buscar"
        title="Encontre pessoas, discos e cidades"
        subtitle="Uma busca para tudo. Pesquise por nome, @usuário, álbum, artista ou cidade."
      />
      <div className="mx-auto max-w-5xl px-4 py-8 lg:px-8">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            navigate({ search: (prev: SearchParams) => ({ ...prev, q: input.trim() }) });
          }}
          className="relative mb-6"
        >
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            autoFocus
            placeholder="Tente: jazz, Miles Davis, São Paulo, @lola..."
            className="h-14 w-full rounded-full border border-input bg-surface pl-12 pr-32 text-base placeholder:text-muted-foreground focus:border-primary focus:outline-none"
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full gradient-primary px-5 py-2.5 text-sm font-bold text-primary-foreground"
          >
            Buscar
          </button>
        </form>

        {q.length >= 2 && (
          <div className="mb-6 flex gap-1 border-b border-border">
            <TabBtn
              active={tab === "people"}
              onClick={() => navigate({ search: (p: SearchParams) => ({ ...p, tab: "people" as const }) })}
              icon={Users}
              label={`Pessoas${people.length ? ` · ${people.length}` : ""}`}
            />
            <TabBtn
              active={tab === "albums"}
              onClick={() => navigate({ search: (p: SearchParams) => ({ ...p, tab: "albums" as const }) })}
              icon={Disc3}
              label={`Álbuns${albums.length ? ` · ${albums.length}` : ""}`}
            />
          </div>
        )}

        {q.length >= 2 && artistList.length > 0 && (
          <section className="mb-8">
            <h2 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              Artistas
            </h2>
            <ul className="flex gap-4 overflow-x-auto pb-2">
              {artistList.map((a) => (
                <li key={a.slug} className="shrink-0">
                  <Link
                    to="/artistas/$slug"
                    params={{ slug: a.slug }}
                    search={{ n: a.name } as never}
                    className="group flex w-20 flex-col items-center gap-2 text-center"
                  >
                    <span className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border border-border bg-surface transition-colors group-hover:border-primary/50">
                      {a.imageUrl ? (
                        <img
                          src={a.imageUrl}
                          alt={a.name}
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Mic2 className="h-7 w-7 text-muted-foreground" />
                      )}
                    </span>
                    <span className="line-clamp-2 text-xs font-semibold leading-tight">{a.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {q.length < 2 ? (
          <p className="text-sm text-muted-foreground">Digite ao menos 2 letras.</p>
        ) : isFetching ? (
          <p className="text-sm text-muted-foreground">Buscando…</p>
        ) : tab === "people" ? (
          people.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma pessoa encontrada.</p>
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2">
              {people.map((p) => (
                <li key={p.id}>
                  <Link
                    to="/profile/$username"
                    params={{ username: p.username }}
                    className="flex items-center gap-3 rounded-2xl border border-border bg-surface/60 p-3 transition-colors hover:border-primary/40"
                  >
                    <img loading="lazy" decoding="async"
                      src={p.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${p.username}`}
                      alt=""
                      className="h-12 w-12 rounded-full object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1 truncate text-sm font-semibold">
                        {p.display_name || p.username}
                        {p.is_premium && <ProBadge size="xs" />}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">@{p.username}</div>
                      {(p.city || p.country) && (
                        <div className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {[p.city, p.country].filter(Boolean).join(", ")}
                        </div>
                      )}
                    </div>
                    <div className="text-right text-xs">
                      <div className="font-display text-base font-bold">{p.owned_count}</div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">discos</div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )
        ) : albums.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum álbum encontrado.</p>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {albums.map((a) => (
              <Link
                key={a.id}
                to="/album/$id"
                params={{ id: buildAlbumParam(String(a.discogs_id ?? a.id), a.artist, a.title) }}
                className="group"
              >
                <div className="aspect-square overflow-hidden rounded-lg bg-surface">
                  {a.cover_url ? (
                    <img src={a.cover_url} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-muted-foreground">Sem capa</div>
                  )}
                </div>
                <div className="mt-2 truncate text-sm font-semibold">{a.title}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {a.artist}
                  {a.year ? ` · ${a.year}` : ""}
                </div>
                <div className="mt-0.5 text-[10px] text-primary">{a.owners} {a.owners === 1 ? "colecionador" : "colecionadores"}</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function TabBtn({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Search;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
        active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}
